import { useMemo } from 'react';
import { fetchNodeAggregate } from '../../api';
import { buildNodeParams } from './nodeFilterUtils';
import useNodeRequest from './useNodeRequest';

/**
 * Загрузка агрегата по выходу узла — одна на все типы графиков.
 *
 * Раньше этот блок был скопирован в каждый компонент графика, и копии успели разойтись:
 * чек-лист не читал `skippedCalls`, а тексты ошибок отличались. С ростом числа типов
 * графиков расхождение гарантированно продолжилось бы.
 */
const useNodeAggregate = ({ chart, dateRange, callType, nodeFilter, packVersion, scope }) => {
  const params = useMemo(
    () => buildNodeParams({ chart, dateRange, callType, nodeFilter, packVersion, scope }),
    [chart, dateRange, callType, nodeFilter, packVersion, scope]
  );

  const { data, isLoading, error, reload } = useNodeRequest({
    fetcher: fetchNodeAggregate,
    params,
    errorText: 'Не удалось загрузить данные по узлу.',
    enabled: Boolean(params.date_from && params.date_to)
  });

  // Нормализуем здесь, а не в каждом рендерере: дальше по коду `calls`/`share` уже числа,
  // а подпись гарантированно непустая.
  const rows = useMemo(
    () =>
      (Array.isArray(data?.items) ? data.items : []).map((item) => ({
        code: item.code,
        label: item.label || item.code,
        calls: Number(item.calls) || 0,
        share: Number(item.share) || 0,
        avgScore: item.avgScore ?? null,
        passRate: item.passRate ?? null,
        naCalls: item.naCalls ?? 0
      })),
    [data]
  );

  // Версии пакета, которыми собрана выдача. Выдача по ним не фильтруется — это
  // справочная величина: история может быть посчитана несколькими наборами нод,
  // и пользователь должен видеть, что цифры собраны не одним пакетом.
  const packVersions = useMemo(
    () => (Array.isArray(data?.packVersions) ? data.packVersions.map(Number).filter(Number.isFinite) : []),
    [data]
  );

  return {
    rows,
    denominator: data?.denominatorCalls ?? 0,
    skipped: data?.skippedCalls ?? 0,
    // Звонки, у которых остались только скрытые с графика значения: сервер уже вычел их из
    // знаменателя, а карточка говорит о них отдельно.
    hiddenCalls: data?.hiddenCalls ?? 0,
    packVersions,
    isLoading,
    error,
    reload
  };
};

export default useNodeAggregate;
